"use client";

import React from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../services/store";
import ERSymbolRow from "./ERSymbolRow";
import TabList from "./TabList";
import { sortBySectorOrder } from "./symbols";
import {
  filterSymbolsByMarketCap,
  sortSectorSymbolsByPercentChange,
} from "./util-symbol-grouping";

/**
 * Props:
 *  - obj: Array<{ sector: string, symbols: string[], count: number }>
 *  - colCh?: number (fixed sector column in "ch")
 */
const getERSymbol = (symInfo) =>
  typeof symInfo === "object" && symInfo !== null ? symInfo.symbol : symInfo;

const getMarketCap = (marketCapMap, symInfo) => {
  const symbol = getERSymbol(symInfo);
  if (!symbol) return 0;

  return (
    marketCapMap?.[symbol] ??
    marketCapMap?.[symbol.toLowerCase()] ??
    marketCapMap?.[symbol.toUpperCase()] ??
    0
  );
};

export default observer(function ERBlock({ obj = [] }) {
  const store = useStore();
  const [symOpenMap, setSymOpenMap] = React.useState({}); // { [symbol]: boolean }
  const [activeFilter, setActiveFilter] = React.useState("0");

  const rows = React.useMemo(() => {
    if (!store.isMarketCapFilter || !Array.isArray(obj)) return obj;

    return obj
      .map((row) => {
        const symbols = Array.isArray(row?.symbols) ? row.symbols : [];
        const filteredSymbols = filterSymbolsByMarketCap(
          symbols.map((symInfo) => [getERSymbol(symInfo), symInfo]),
          store.marketCapMap,
        ).map(([, symInfo]) => symInfo);

        return { ...row, symbols: filteredSymbols };
      })
      .filter((row) => row.symbols.length > 0);
  }, [obj, store.isMarketCapFilter, store.marketCapMap]);

  const erCounts = React.useMemo(() => {
    const counts = { all: 0, "-1": 0, 0: 0, 1: 0 };
    if (!Array.isArray(rows)) return counts;
    rows.forEach((row) => {
      const symbols = row?.symbols;
      if (!Array.isArray(symbols)) return;
      symbols.forEach((symInfo) => {
        counts.all += 1;
        if (typeof symInfo === "object" && symInfo !== null) {
          const key = String(symInfo.erVal);
          if (Object.prototype.hasOwnProperty.call(counts, key)) {
            counts[key] += 1;
          }
        }
      });
    });
    return counts;
  }, [rows]);

  const erTabs = [
    { id: "all", label: "All", badge: erCounts.all },
    { id: "-1", label: "Yesterday", badge: erCounts["-1"] },
    { id: "0", label: "Today", badge: erCounts["0"] },
    { id: "1", label: "Tomorrow", badge: erCounts["1"] },
  ];

  if (!rows.length) return null;

  const toggleSym = (sym) => {
    setSymOpenMap((m) => ({ ...m, [sym]: !m[sym] }));
    store.setAllowShortCut(true);
  };

  return (
    <div
      className="font-mono text-sm whitespace-pre leading-5 near-white bg-near-black"
      style={{
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: 14,
        lineHeight: 1.5,
        borderRadius: 8,
        padding: 8,
        boxShadow: "0 2px 6px rgba(0,0,0,.25)",
      }}
    >
      <div style={{ marginBottom: 8 }}>
        <TabList
          tabs={erTabs}
          activeId={activeFilter}
          onChange={setActiveFilter}
          visibleCountMobile={4}
        />
      </div>
      <div
        style={{
          marginTop: 4,
          padding: 6,
          border: "1px solid rgba(255,255,255,0.15)",
          borderRadius: 6,
          background: "rgba(31,41,55,.4)", // dark gray w/ translucency
        }}
      >
        {(() => {
          const filteredEntries = rows.flatMap(({ sector, symbols = [] }) => {
            // Keep Today/Tomorrow ER lists independent from the percentage threshold toggle.
            if (
              activeFilter !== "0" &&
              activeFilter !== "1" &&
              store.filterByPercentageThreshold &&
              symbols.length < 3
            ) {
              return [];
            }

            return symbols
              .filter((symInfo) => {
                if (activeFilter === "all") return true;
                if (typeof symInfo === "object" && symInfo !== null) {
                  return String(symInfo.erVal) === activeFilter;
                }
                return false;
              })
              .map((symInfo) => ({ sector, symInfo }));
          });

          const sortedEntries = !store.strongSort
            ? sortSectorSymbolsByPercentChange(
                filteredEntries.map(({ sector, symInfo }) => {
                  const symbol = getERSymbol(symInfo);
                  return [symbol, { sector, symInfo }];
                }),
                store.currentGridMap,
                store.formattedDate,
                store.percentSortDesc,
              ).map(([symbol, { sector, symInfo }]) => ({ sector, symInfo }))
            : sortBySectorOrder(
                filteredEntries,
                (item) => item.sector,
                (a, b) =>
                  getMarketCap(store.marketCapMap, b.symInfo) -
                  getMarketCap(store.marketCapMap, a.symInfo),
              );

          const renderSymbol = ({ sector, symInfo }) => {
            const symKey = getERSymbol(symInfo);
            const sOpen = !!symOpenMap[symKey];
            return (
              <ERSymbolRow
                key={`${sector}-${symKey}`}
                symInfo={symInfo}
                isOpen={sOpen}
                onToggle={toggleSym}
                sector={sector}
                erData={rows} // Pass the full ER data for related symbols
              />
            );
          };

          if (!store.strongSort) {
            return sortedEntries.map(renderSymbol);
          }

          const sectorGroups = sortedEntries.reduce((groups, entry) => {
            const sector = entry.sector || "other";
            if (!groups.has(sector)) groups.set(sector, []);
            groups.get(sector).push(entry);
            return groups;
          }, new Map());

          return Array.from(sectorGroups.entries()).map(
            ([sector, entries], groupIndex) => (
              <div key={sector}>
                {groupIndex > 0 && <div style={{ height: 8 }} />}
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "rgba(255,255,255,0.6)",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    marginBottom: 4,
                    paddingLeft: 4,
                    borderLeft: "2px solid rgba(255,255,255,0.2)",
                  }}
                >
                  {sector}
                </div>
                {entries.map(renderSymbol)}
              </div>
            ),
          );
        })()}
      </div>
    </div>
  );
});
